import {ApiClient, type ErrorResponse} from '@hconnect/apiclient'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {useMutation, type UseMutationOptions, useQueryClient} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useErrorHandler} from 'react-error-boundary'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {notifyIfErrorMessage} from '../utils'

type Variables = {
  plantCode: string
  materialOrderId: number
  attachmentIds: number[]
}

export const useDeleteMaterialOrderAttachments = (
  options?: Omit<UseMutationOptions<void, AxiosError<ErrorResponse>, Variables>, 'mutationFn'>
) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const raiseError = useErrorHandler()
  const queryClient = useQueryClient()
  return useMutation<void, AxiosError<ErrorResponse>, Variables>({
    ...options,
    mutationKey: ['deleteMaterialOrderAttachment'],
    mutationFn: async ({plantCode, materialOrderId, attachmentIds}) => {
      await Promise.all(
        attachmentIds.map((id) =>
          ApiClient.axiosInstance.delete<void>(
            `/plants/${plantCode}/material-orders/${materialOrderId}/attachments/${id}`
          )
        )
      )
    },
    onError: (error) => {
      notifyIfErrorMessage(error.response?.data.detail, raiseError, spawnErrorToast)
    },
    onSuccess: () => {
      spawnSuccessToast(t('success.deleteMaterialOrderAttachment'))
    },
    onSettled: () => {
      void queryClient.invalidateQueries({queryKey: ['materialOrders'], refetchType: 'all'})
    }
  })
}
